import React, { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Check, Save } from 'lucide-react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'

interface UserSettings {
  default_source_language: string
  default_target_language: string
  tm_min_score: number
  default_mt_provider: string
  items_per_page: number
  spellcheck_enabled: boolean
  auto_save_interval: number
}

const DEFAULT_SETTINGS: UserSettings = {
  default_source_language: 'en',
  default_target_language: 'pt-PT',
  tm_min_score: 70,
  default_mt_provider: 'deepl',
  items_per_page: 25,
  spellcheck_enabled: true,
  auto_save_interval: 3,
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'pt-PT', label: 'Portuguese (PT)' },
  { code: 'pt-br', label: 'Portuguese (BR)' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'ar', label: 'Arabic' },
]

const MT_PROVIDERS = [
  { value: 'deepl', label: 'DeepL' },
  { value: 'google', label: 'Google Translate' },
  { value: 'mymemory', label: 'MyMemory (Free)' },
]

export default function SettingsPage() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS)
  const [saving, setSaving] = useState(false)
  const { theme, setTheme } = useTheme()
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    try {
      const stored = localStorage.getItem('translyxcat_settings_' + user.id)
      if (stored) setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(stored) })
    } catch {}
  }, [user])

  const save = () => {
    if (!user?.id) return
    setSaving(true)
    localStorage.setItem('translyxcat_settings_' + user.id, JSON.stringify(settings))
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2500) }, 400)
  }

  // Old links to /settings?tab=billing, and PayFast returns still pointing at
  // /settings?payfast=..., now live at /billing. The plan and payfast values are kept.
  if (searchParams.get('tab') === 'billing' || searchParams.get('payfast')) {
    const forward = new URLSearchParams()
    for (const key of ['plan', 'payfast']) {
      const value = searchParams.get(key)
      if (value) forward.set(key, value)
    }
    const query = forward.toString()
    return <Navigate to={query ? `/billing?${query}` : '/billing'} replace />
  }

  const set = (key: keyof UserSettings, value: any) =>
    setSettings(prev => ({ ...prev, [key]: value }))

  const sel = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gold-500 bg-white min-w-[180px]'

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-700 text-sm mt-1">Personalise your TranslyxCAT experience</p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-5 py-2 rounded-lg bg-gold-600 hover:bg-gold-500 text-white text-sm font-medium disabled:opacity-60 transition-colors">
          {saved ? <><Check className="w-4 h-4" /><span>Saved</span></>
            : saving ? <><div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" /><span>Saving</span></>
            : <><Save className="w-4 h-4" /><span>Save Settings</span></>}
        </button>
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Appearance</h2>
          <p className="text-sm text-gray-800 mb-4">Choose how TranslyxCAT looks on your device</p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Theme</span>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {(["light", "dark", "system"] as const).map(t => (
                <button key={t} onClick={() => setTheme(t)}
                  className={"px-4 py-1.5 rounded-md text-sm font-medium transition-all " + (
                    theme === t ? "bg-white text-slate-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  )}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Language Defaults</h2>
          <p className="text-sm text-gray-800 mb-4">Default languages when creating new projects</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Default source language</span>
              <select value={settings.default_source_language} onChange={e => set('default_source_language', e.target.value)} className={sel}>
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-700">Default target language</div>
                <div className="text-xs text-gray-800">pt-PT = European, pt-BR = Brazilian</div>
              </div>
              <select value={settings.default_target_language} onChange={e => set('default_target_language', e.target.value)} className={sel}>
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Translation Memory</h2>
          <p className="text-sm text-gray-800 mb-4">Control TM match filtering</p>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700">Minimum match score</div>
              <div className="text-xs text-gray-800">Segments below this won't show as TM suggestions</div>
            </div>
            <div className="flex items-center gap-3">
              <input type="range" min={50} max={100} step={5} value={settings.tm_min_score}
                onChange={e => set('tm_min_score', Number(e.target.value))} className="w-32 accent-gold-500" />
              <span className="text-sm font-bold text-gold-600 w-10">{settings.tm_min_score}%</span>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Machine Translation</h2>
          <p className="text-sm text-gray-800 mb-4">Default MT provider</p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Default provider</span>
            <select value={settings.default_mt_provider} onChange={e => set('default_mt_provider', e.target.value)} className={sel}>
              {MT_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Editor</h2>
          <p className="text-sm text-gray-800 mb-4">Customise your editing experience</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Segments per page</span>
              <select value={settings.items_per_page} onChange={e => set('items_per_page', Number(e.target.value))} className={sel}>
                {[10,25,50,100].map(n => <option key={n} value={n}>{n} per page</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Auto-save interval</span>
              <select value={settings.auto_save_interval} onChange={e => set('auto_save_interval', Number(e.target.value))} className={sel}>
                {[1,2,3,5,10].map(n => <option key={n} value={n}>Every {n}s</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Spell check on by default</span>
              <button onClick={() => set('spellcheck_enabled', !settings.spellcheck_enabled)}
                className={'relative inline-flex h-6 w-11 items-center rounded-full transition-colors ' + (settings.spellcheck_enabled ? 'bg-gold-500' : 'bg-gray-300')}>
                <span className={'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' + (settings.spellcheck_enabled ? 'translate-x-6' : 'translate-x-1')} />
              </button>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Account</h2>
          <p className="text-sm text-gray-800 mb-4">Your account information</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Email</span>
              <span className="text-sm text-gray-700">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">User ID</span>
              <span className="text-xs text-gray-800 font-mono">{user?.id?.slice(0,16)}...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
